<template>
  <div>
    <vs-button @click="goBack" color="dark" type="filled" icon-pack="feather" icon="icon-arrow-left"> Back </vs-button>
    <div class="hotel-dashboard">
      <div class="w-full px-4 my-5">
        <!-- Header -->
        <div class="flex justify-between items-center mb-3">

        </div>
        <div>
          <h3 class="text-2xl font-semibold capitalize">{{ dashboardTitle }}</h3>
          <p class="text-gray-600 mt-1" style="font-size: 18px !important;">Room availability and income statistics</p>
        </div>
        <vs-divider></vs-divider>

        <!-- Date Range Filter -->
        <div class="flex gap-4 mb-6 mt-4">
          <div class="vx-col ">
            <label class="text-base font-semibold mb-2 block">Start Date</label>

            <flat-pickr v-model="hotelStartDate" :config="datePickerConfig" placeholder="Select Start Date"
              class="w-full daterange-picker" @on-change="onStartDateChange" />

          </div>
          <div class="vx-col ">
            <label class="text-base font-semibold mb-2 block">End Date</label>
            <flat-pickr v-model="hotelEndDate" :config="endDatePickerConfig" placeholder="Select End Date"
              class="w-full daterange-picker" @on-change="fetchAnalyticsData" />

          </div>
          <div class="vx-col  flex items-end">
            <vs-button @click="resetDateFilter" type="filled" size="medium">
              Reset Filter
            </vs-button>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-12">
          <vs-progress indeterminate color="primary"></vs-progress>
          <p class="mt-4 text-gray-600">Loading dashboard data...</p>
        </div>

        <!-- Dashboard Content -->
        <div v-else-if="dashboardData">
          <!-- Statistics Cards -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <!-- Total Rooms -->
            <div class="stat-card bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="room-info-lebel">Total Rooms</p>
                  <h3 class="text-3xl font-bold text-blue-600">
                    {{ dashboardData.room_statistics ? dashboardData.room_statistics.total_rooms : 0 }}
                  </h3>
                </div>
                <feather-icon icon="HomeIcon" svgClasses="h-8 w-8 text-blue-400" />
              </div>
            </div>

            <!-- Booked Rooms -->
            <div class="stat-card bg-orange-50 border border-orange-200 rounded-lg p-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="room-info-lebel">Booked Rooms</p>
                  <h3 class="text-3xl font-bold text-orange-600">
                    {{ dashboardData.room_statistics ? dashboardData.room_statistics.booked_rooms : 0 }}
                  </h3>
                </div>
                <feather-icon icon="LockIcon" svgClasses="h-8 w-8 text-orange-400" />
              </div>
            </div>

            <!-- Available Rooms -->
            <div class="stat-card bg-green-50 border border-green-200 rounded-lg p-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="room-info-lebel">Available Rooms</p>
                  <h3 class="text-3xl font-bold text-green-600">
                    {{ dashboardData.room_statistics ? dashboardData.room_statistics.available_rooms : 0 }}
                  </h3>
                </div>
                <feather-icon icon="UnlockIcon" svgClasses="h-8 w-8 text-green-400" />
              </div>
            </div>

            <!-- Occupancy Rate -->
            <div class="stat-card bg-purple-50 border border-purple-200 rounded-lg p-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="room-info-lebel">Occupancy Rate</p>
                  <h3 class="text-3xl font-bold text-purple-600">
                    {{ dashboardData.room_statistics ? dashboardData.room_statistics.occupancy_rate : 0 }}%
                  </h3>
                </div>
                <feather-icon icon="TrendingUpIcon" svgClasses="h-8 w-8 text-purple-400" />
              </div>
            </div>
          </div>

          <!-- Income Statistics -->
          <div class="mb-6">
            <h4 class="font-semibold mb-4" style="font-size: 21px !important;">Income Statistics</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <!-- Total Revenue -->
              <div class="income-stat-card income-stat-card-green bg-white rounded-lg p-6 shadow-md">
                <div class="flex items-center justify-between mb-4">
                  <div class="icon-container bg-green-100 rounded-lg ">
                    <p style="color: #10b981 !important;" class=" text-base font-semibold">Total Revenue</p>
                  </div>
                </div>
                <h3 class="text-3xl font-bold text-gray-800 mb-2">Rs. {{ formatCurrency(dashboardData.income_statistics
                  ? dashboardData.income_statistics.total_revenue : 0) }}</h3>
                <p class="text-gray-500  income-booking">{{ dashboardData.income_statistics ?
                  dashboardData.income_statistics.total_bookings : 0 }} Bookings</p>
              </div>

              <!-- Confirmed -->
              <div class="income-stat-card income-stat-card-blue bg-white rounded-lg p-6 shadow-md">
                <div class="flex items-center justify-between mb-4">
                  <div class="icon-container bg-blue-100 rounded-lg ">
                    <p style="color: #3b82f6 !important;" class=" text-base font-semibold">Confirmed</p>
                  </div>
                </div>
                <h3 class="text-3xl font-bold text-gray-800 mb-2">Rs. {{ formatCurrency(dashboardData.income_statistics
                  ? dashboardData.income_statistics.confirmed_revenue : 0) }}</h3>
                <p class="text-gray-500  income-booking">{{ dashboardData.income_statistics ?
                  dashboardData.income_statistics.confirmed_bookings : 0 }} Bookings</p>
              </div>

              <!-- Pending -->
              <div class="income-stat-card income-stat-card-yellow bg-white rounded-lg p-6 shadow-md">
                <div class="flex items-center justify-between mb-4">
                  <div class="icon-container bg-yellow-100 rounded-lg ">
                    <p style="color: #eab308 !important;" class=" text-base font-semibold">Pending</p>
                  </div>
                </div>
                <h3 class="text-3xl font-bold text-gray-800 mb-2">Rs. {{ formatCurrency(dashboardData.income_statistics
                  ? dashboardData.income_statistics.pending_revenue : 0) }}</h3>
                <p class="text-gray-500 income-booking">{{ dashboardData.income_statistics ?
                  dashboardData.income_statistics.pending_bookings : 0 }} Bookings</p>
              </div>

              <!-- Cancelled -->
              <div class="income-stat-card income-stat-card-red bg-white rounded-lg p-6 shadow-md">
                <div class="flex items-center justify-between mb-4">
                  <div class="icon-container bg-red-100 rounded-lg ">
                    <p style="color: #ef4444 !important;" class=" text-base font-semibold">Cancelled</p>
                  </div>
                </div>
                <h3 class="text-3xl font-bold text-gray-800 mb-2">Rs. {{ formatCurrency(dashboardData.income_statistics
                  ? dashboardData.income_statistics.cancelled_revenue : 0) }}</h3>
                <p class="text-gray-500  income-booking">{{ dashboardData.income_statistics ?
                  dashboardData.income_statistics.cancelled_bookings : 0 }} Bookings</p>
              </div>

              <!-- Completed -->
              <div class="income-stat-card income-stat-card-indigo bg-white rounded-lg p-6 shadow-md">
                <div class="flex items-center justify-between mb-4">
                  <div class="icon-container bg-indigo-100 rounded-lg ">
                    <p style="color: #6366f1 !important;" class=" text-base font-semibold">Completed</p>
                  </div>
                </div>
                <h3 class="text-3xl font-bold text-gray-800 mb-2">Rs. {{ formatCurrency(dashboardData.income_statistics
                  ? dashboardData.income_statistics.completed_revenue : 0) }}</h3>
                <p class="text-gray-500  income-booking">{{ dashboardData.income_statistics ?
                  dashboardData.income_statistics.completed_bookings : 0 }} Bookings</p>
              </div>
            </div>
          </div>

          <!-- Room Details Table -->
          <div class="bg-white border rounded-lg p-6 mb-6">
            <h4 class=" font-semibold mb-4" style="font-size: 21px !important;">Room Availability Details</h4>
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead>
                  <tr class="border-b">
                    <th class="text-left text-base p-3 font-semibold">Room Name</th>
                    <th class="text-left text-base p-3 font-semibold">Room Type</th>
                    <th class="text-center text-base p-3 font-semibold">Price/Night</th>
                    <th class="text-center text-base p-3 font-semibold">Total</th>
                    <th class="text-center text-base p-3 font-semibold">Booked</th>
                    <th class="text-center text-base p-3 font-semibold">Available</th>
                    <th class="text-center text-base p-3 font-semibold">Active/Inactive</th>
                    <th class="text-left text-base p-3 pl-8 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="room in dashboardData.room_details" :key="room.room_id" class="border-b hover:bg-gray-50">
                    <td class="p-3 capitalize text-base">{{ room.room_name }}</td>
                    <td class="p-3">
                      <span class="px-2 py-1 bg-gray-100 rounded text-base">{{ formatRoomType(room.room_type) }}</span>
                    </td>
                    <td class="p-3 text-center text-base">Rs. {{ formatCurrency(room.price_per_night) }}</td>
                    <td class="p-3 text-center font-semibold text-base">{{ room.total_rooms }}</td>
                    <td class="p-3 text-center">
                      <span class="text-orange-600 font-semibold text-base">{{ room.booked_rooms }}</span>
                    </td>
                    <td class="p-3 text-center">
                      <span class="text-green-600 font-semibold text-base">{{ room.available_rooms }}</span>
                    </td>
                    <td class="p-3 text-center flex justify-center items-center" style="height:67.53px !important;">
                      <vs-switch v-model="room.is_active" @input="toggleRoomStatus(room, $event)" color="success"
                        class="ml-1" :disabled="room.booked_rooms > 0 && !room.is_active"></vs-switch>

                    </td>
                    <td class="p-3 text-center">

                      <vs-chip :color="room.is_available ? 'success' : 'danger'" v-if="room.is_available">
                        Available
                      </vs-chip>
                      <vs-chip :color="room.is_available ? 'success' : 'danger'" v-else>
                        Booked
                      </vs-chip>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Recent Bookings -->
          <div class="bg-white border rounded-lg p-6">
            <h4 class="text-lg font-semibold mb-4" style="font-size: 21px !important;">Recent Bookings</h4>
            <div v-if="dashboardData.recent_bookings && dashboardData.recent_bookings.length > 0"
              class="overflow-x-auto">
              <table class="w-full">
                <thead>
                  <tr class="border-b">
                    <th class="text-left text-base p-3 font-semibold">User</th>
                    <th class="text-left text-base p-3 font-semibold">Check In</th>
                    <th class="text-left text-base p-3 font-semibold">Check Out</th>
                    <th class="text-center text-base p-3 font-semibold">Nights</th>
                    <th class="text-center text-base p-3 font-semibold">Amount</th>
                    <th class="text-center text-base p-3 font-semibold">Status</th>
                    <th class="text-left text-base p-3 font-semibold">Payment</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="booking in dashboardData.recent_bookings" :key="booking._id"
                    class="border-b hover:bg-gray-50">
                    <td class="p-3">
                      <div v-if="booking.user">
                        <p class="font-semibold capitalize text-base">{{ booking.user.first_name }} {{
                          booking.user.last_name }}</p>
                        <p class=" text-gray-600 text-base">{{ booking.user.email }}</p>
                      </div>
                      <span v-else class="text-gray-400">N/A</span>
                    </td>
                    <td class="p-3 text-base">{{ formatDate(booking.check_in_date) }}</td>
                    <td class="p-3 text-base">{{ formatDate(booking.check_out_date) }}</td>
                    <td class="p-3 text-center text-base">{{ booking.number_of_nights }}</td>
                    <td class="p-3 text-center font-semibold text-base">Rs. {{ formatCurrency(booking.total_amount) }}
                    </td>
                    <td class="p-3 text-center text-base  flex justify-center">
                      <vs-chip :color="getStatusColor(booking.status)">{{ booking.status }}</vs-chip>
                    </td>
                    <td class="p-3 text-center text-base">
                      <vs-chip :color="getPaymentStatusColor(booking.payment_status)">{{ booking.payment_status
                      }}</vs-chip>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="text-center text-base py-8 text-gray-500">
              No Bookings Found
            </div>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="text-center py-12">
          <p class="text-red-600 mb-4">{{ error }}</p>
          <vs-button @click="loadDashboard" color="primary">Retry</vs-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axios'
import 'flatpickr/dist/flatpickr.css'
import flatPickr from 'vue-flatpickr-component'

export default {
  name: 'HotelDashboard',
  components: {
    flatPickr
  },
  data() {
    return {
      hotelId: null,
      hotelStartDate: null,
      hotelEndDate: null,
      loading: false,
      error: null,
      dashboardData: null,
      startDate: '',
      endDate: '',
      datePickerConfig: {
        wrap: true,
        dateFormat: 'd-m-Y',
        maxDate: new Date()
      },
      isTogglingRoom: false,
    }
  },
  computed: {
    endDatePickerConfig() {
      const config = {
        ...this.datePickerConfig
      }

      // Set minDate to the selected start date if it exists
      if (this.hotelStartDate) {
        const minDate = this.parseDate(this.hotelStartDate)
        if (minDate) {
          config.minDate = minDate
        }
      }

      return config
    },
    routeHotelId() {
      return this.$route.params.hotelId || this.$route.params.id || null
    },
    dashboardTitle() {
      const d = this.dashboardData
      if (d && d.hotel && d.hotel.name) {
        return d.hotel.name
      }
      return 'Hotel Dashboard'
    }
  },
  watch: {
    routeHotelId: {
      immediate: true,
      handler() {
        this.applyResolvedHotelId()
      }
    }
  },
  methods: {
    readStoredHotelId() {
      try {
        const raw = localStorage.getItem('hotel')
        if (!raw) return null
        const hotel = JSON.parse(raw)
        return hotel && hotel._id != null ? String(hotel._id) : null
      } catch (e) {
        return null
      }
    },
    applyResolvedHotelId() {
      const fromRoute = this.routeHotelId
      const next = fromRoute ? String(fromRoute) : this.readStoredHotelId()
      if (next === this.hotelId) {
        if (this.hotelId && !this.dashboardData && !this.loading && !this.error) {
          this.loadDashboard()
        }
        return
      }
      this.hotelId = next
      this.dashboardData = null
      this.error = null
      if (this.hotelId) {
        this.loadDashboard()
      }
    },
    async loadDashboard() {
      if (!this.hotelId) return

      this.loading = true
      this.error = null

      try {
        const params = {}
        if (this.hotelStartDate) params.startDate = this.hotelStartDate
        if (this.hotelEndDate) params.endDate = this.hotelEndDate


        const response = await axios.get(`/admin/hotel/${this.hotelId}/dashboard`, { params })


        if (response.data.success) {
          this.dashboardData = response.data.data
        } else {
          this.error = response.data.message || 'Failed to load dashboard data'
        }
      } catch (error) {
        console.error('Error loading dashboard:', error)
        this.error = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Failed to load dashboard data'
      } finally {
        this.loading = false
      }
    },
    onStartDateChange(selectedDates) {
      if (this.hotelStartDate && this.hotelEndDate) {
        const startDate = this.parseDate(this.hotelStartDate)
        const endDate = this.parseDate(this.hotelEndDate)

        if (endDate && startDate && endDate < startDate) {
          this.hotelEndDate = null
        }
      }
      this.loadDashboard()
    },
    parseDate(dateValue) {
      if (!dateValue) return null
      if (dateValue instanceof Date) {
        return dateValue
      }
      if (typeof dateValue === 'string') {
        // Parse string date in d-m-Y format
        const parts = dateValue.split('-')
        if (parts.length === 3) {
          return new Date(parts[2], parts[1] - 1, parts[0]) // Year, Month (0-indexed), Day
        }
        return new Date(dateValue)
      }
      return new Date(dateValue)
    },
    fetchAnalyticsData(selectedDates) {
      this.loadDashboard()
    },
    formatDateForAPI(date) {
      if (!date) return ''
      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    resetDateFilter() {
      this.startDate = ''
      this.endDate = ''
      this.hotelStartDate = null
      this.hotelEndDate = null
      this.loadDashboard()
    },
    goBack() {
      const n = this.$route.name
      if (n === 'hotel-dashboard-by-id') {
        this.$router.push('/hotel/list')
        return
      }
      if (n === 'gurudwara-hotel-dashboard') {
        const gw = this.$route.params.gurudwaraId
        if (gw) {
          this.$router.push(`/gurudwara/${  gw  }/edit`)
          return
        }
      }
      if (n === 'hotel-dashboard') {
        this.$router.push('/hotel/list')
        return
      }
      this.$router.go(-1)
    },
    formatCurrency(amount) {
      if (!amount) return '0'
      return new Intl.NumberFormat('en-IN').format(amount)
    },
    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    formatRoomType(type) {
      const types = {
        '1_bed_ac': '1 Bed AC',
        '1_bed_non_ac': '1 Bed Non-AC',
        '2_bed_ac': '2 Bed AC',
        '2_bed_non_ac': '2 Bed Non-AC',
        dormitory_hall: 'Dormitory Hall'
      }
      return types[type] || type
    },
    getStatusColor(status) {
      const colors = {
        pending: 'warning',
        confirmed: 'success',
        cancelled: 'danger',
        completed: 'primary'
      }
      return colors[status] || 'primary'
    },
    getPaymentStatusColor(status) {
      const colors = {
        pending: 'warning',
        paid: 'success',
        refunded: 'danger'
      }
      return colors[status] || 'primary'
    },
    async toggleRoomStatus(room, newStatus) {
      // Prevent duplicate calls
      if (this.isTogglingRoom) {
        return
      }

      // Use the event value if provided, otherwise use room.is_active
      const status = newStatus !== undefined ? newStatus : room.is_active

      this.isTogglingRoom = true

      // Check if trying to deactivate room with bookings
      if (!status && room.booked_rooms > 0) {
        room.is_active = true // Revert the switch
        this.$vs.notify({
          title: 'Cannot Deactivate',
          text: 'Cannot deactivate room with active bookings',
          color: 'warning'
        })
        this.isTogglingRoom = false
        return
      }

      const action = status ? 'activate' : 'deactivate'

      try {
        await axios.put(`/admin/hotel/${this.hotelId}/room/${room.room_id}/status`, {
          is_active: status
        })

        this.$vs.notify({
          title: 'Success',
          text: `Room ${action}d successfully`,
          color: 'success'
        })

        // Reload dashboard to get updated data
        await this.loadDashboard()
      } catch (error) {
        // Revert the switch on error
        room.is_active = !status

        this.$vs.notify({
          title: 'Error',
          text: error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : `Failed to ${action} room`,
          color: 'danger'
        })
      } finally {
        this.isTogglingRoom = false
      }
    }
  }
}
</script>

<style scoped>
.hotel-dashboard {
  min-height: 100vh;
  background: #f8f9fa;
  padding-bottom: 12px;
}

.text-base {
  font-size: 16px !important;
}

.daterange-picker {
  width: 200px !important;
}

.stat-card {
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* .stat-card:hover {
  transform: translateY(-2px);
} */

.income-stat-card {
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
  border-bottom: 4px solid transparent;
}

.income-stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
}

.income-booking {
  font-size: 16px !important;
  font-weight: 500 !important;
  color: #6b7280 !important;
}

.income-stat-card-green {
  border-bottom-color: #10b981 !important;
  /* green-500 */
}

.income-stat-card-blue {
  border-bottom-color: #3b82f6 !important;
  /* blue-500 */
}

.income-stat-card-yellow {
  border-bottom-color: #eab308 !important;
  /* yellow-500 */
}

.income-stat-card-red {
  border-bottom-color: #ef4444 !important;
  /* red-500 */
}

.income-stat-card-indigo {
  border-bottom-color: #6366f1 !important;
  /* indigo-500 */
}

.icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.room-info-lebel {
  font-size: 16px !important;
  font-weight: 600 !important;
  margin-bottom: 0.25rem !important;
  color: #1f2937 !important;
}

table {
  border-collapse: collapse;
}

table th,
table td {
  border-bottom: 1px solid #e5e7eb;
}
</style>
